import {Component, OnInit, Inject, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of } from 'rxjs';
import {GetComponent} from './get/get.component';
import { environment } from '../../../environments/environment';
import { first, map } from 'rxjs/operators';
import { AuthenticationService } from '../../services/authentication.service';
import { ConfigurationService } from '../../services/configuration.service';
import { DataPathUtils } from '../../utils/dataPath.utils';
import { UrlUtils } from '../../utils/url.utils';
import { RequestsService } from '../../services/requests.service';
import { DataService } from 'app/services/data.service';
import { CommonUtils } from 'app/utils/common.utils';
import { GlobalConstants } from '../../common/global-constants';

@Component({
  selector: 'app-main-view',
  templateUrl: './main-view.component.html',
  styleUrls: ['./main-view.component.scss']
})
export class MainViewComponent implements OnInit {

  @ViewChild(GetComponent) private getComponent: GetComponent;
  popupState: string = null;

  pageData: any = null;

  config: any = {};

  selectedRow: any = {};

  loading: boolean = false;

  records: any;

  carrierNameShipMethodMap = new Map();

  countryCodeCarrierNameMap = new Map();

  getRecordsUrl: string = '/get-shipmethods/'

  user: any = {};

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastrService: ToastrService,
    @Inject('UrlUtils') private readonly urlUtils: UrlUtils,
    @Inject('DataPathUtils') private readonly dataPathUtils: DataPathUtils,
    @Inject('RequestsService') private readonly requestsService: RequestsService,
    @Inject('ConfigurationService') private readonly configurationService: ConfigurationService,
    @Inject('AuthenticationService') private readonly authenticationService: AuthenticationService,
    @Inject('CommonUtils') private readonly commonUtils: CommonUtils,
    private dataService: DataService
  ) {
  }

  ngOnInit() {
    forkJoin(
      this.configurationService.getConfiguration(),
      this.authenticationService.getAuthenticated()
    ).subscribe(([config, _]) => {
      if (!config || !config.pages) {
        return;
      }
      this.config = config;
      this.user = this.authenticationService.getUserDetails();
      this.loading = true;
      forkJoin([
        this.dataService.getBindlePermission(this.user.alias, 'admin'),
        this.dataService.getBindlePermission(this.user.alias, 'superUser')
      ]).pipe(first()).subscribe(([adminData, superUserData]: any) => {
        this.loading = false;
        // Set permissions in both component and service
        this.dataService.resourcePermission['admin'] = !!adminData?.can_access;
        this.dataService.resourcePermission['superUser'] = !!superUserData?.can_access;
        this.subscribeToRouteParams();
      }, () => {
        this.loading = false;
        // If permission check itself fails, still allow routing (fail open)
        this.toastrService.error(GlobalConstants.SOMETHING_WENT_WRONG, 'Error');
        this.subscribeToRouteParams();
      });
    }, () => {
      this.loading = false;
      this.toastrService.error(GlobalConstants.SOMETHING_WENT_WRONG, 'Error');
    });
  }

  get resourcePermission() {
    return this.dataService.resourcePermission;
  }

  private subscribeToRouteParams(): void {
    this.route.params.pipe(map((p: any) => p.pageId)).subscribe((pageId) => {
      this.getPageData(pageId);
      this.dataService.pageId = pageId;
    });
  }

  private getPageData(pageId) {
    this.pageData = null;

    if (!pageId) {
      this.pageData = this.config.pages.filter(page => page.default)[0];

      if (!this.pageData) {
        this.pageData = this.config.pages[0];
      }

      this.router.navigate([this.pageData.id]);

      return;
    }
    else {
      this.pageData = this.config.pages.filter(page => page.id === pageId)[0];
    }

    if (!this.pageData) {
      this.toastrService.error(`No page found with id ${pageId}`, 'Error');
      return;
    }
  }

  private getRowData(defaultData = {}) {
    if (!this.pageData.methods.getSingle) {
      return of(defaultData);
    }

    const getMethod = this.pageData.methods.getSingle;
    let getUrl = getMethod.url;
    if (!getUrl) {
      return of(defaultData);
    }

    const paramsPath = getMethod.paramsPath;
    getUrl = this.urlUtils.getParsedUrl(getUrl, defaultData, paramsPath);

    if (environment.logApiData) {
      console.log('Get single url', getUrl);
    }

    let actualMethod = this.requestsService.get.bind(this.requestsService);
    const actualMethodType = this.pageData.methods.getSingle.actualMethod;
    if (actualMethodType && this.requestsService[actualMethodType]) {
      actualMethod = this.requestsService[actualMethodType].bind(this.requestsService);
    }

    let requestHeaders = getMethod.requestHeaders;
    if (!requestHeaders) {
      requestHeaders = this.pageData.requestHeaders;
    }

    return actualMethod(getUrl, requestHeaders)
      .pipe(map(res => this.dataPathUtils.extractDataFromResponse(res, getMethod.dataPath || '')));
  }

  public showPopup(e: any = {}) {
    const getMethod = this.pageData.methods.getSingle;
    this.popupState = e.state || null;
    switch (this.popupState) {
      case 'put':
        this.loading = true;
        this.getRowData(e.data || {}).subscribe((res) => {
          if (environment.logApiData) {
            console.log('Single item data', res);
          }
          this.selectedRow = res;
          this.loading = false;
        }, (e) => {
          console.error(e);
          this.toastrService.error(e, 'Error');
          this.selectedRow = e.data || {};
          this.loading = false;
        });
        break;
      case 'afterChange':
        this.getComponent.firstRequest();
        break;
      default:
        this.selectedRow = {};
    }
  }

  get checkAdminPermission() {
    return this.resourcePermission["admin"];
  }

}